import React from 'react';
import {
  Typography,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';

import { CommonCement } from '../common/common-cement';
import { convertToHalfWidthKatakana } from '../../utils/validations/common-validation';
import { PLACEHOLDER_MESSAGES } from '../../utils/constants/placeholder-messages';

import type {
  SupplierRegistrationForm,
  SupplierType,
  SupplierCategory,
} from '../../types/company.types';

import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

interface SupplierInfoSectionProps {
  formData: SupplierRegistrationForm;
  originalFormData?: SupplierRegistrationForm;
  onUpdateField: (field: keyof SupplierRegistrationForm, value: string | boolean) => void;
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
}

export const SupplierInfoSection: React.FC<SupplierInfoSectionProps> = ({
  formData,
  originalFormData,
  onUpdateField,
  getFieldError,
  isFieldValid,
}) => {
  const supplierTypes: SupplierType[] = ['組合', '販売店', 'プラント'];
  const supplierCategories: SupplierCategory[] = [
    'ゼネコン',
    'ハウスメーカー',
    '商社販売店',
    '生コンプラント',
    'その他',
  ];

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 2, paddingBottom: '32px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
        }}
      >
        仕入先基本情報
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 仕入先種別 */}
        <Box>
          <FormControl fullWidth error={!isFieldValid('supplierType')} required>
            <InputLabel>仕入先種別</InputLabel>
            <Select
              value={formData.supplierType || ''}
              onChange={(e) => onUpdateField('supplierType', e.target.value as SupplierType)}
              slotProps={{
                input: {
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.supplierType,
                    formData.supplierType
                  ),
                },
              }}
              label='仕入先種別選択'
            >
              {supplierTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('supplierType')}</FormHelperText>
          </FormControl>
        </Box>

        {/* Gali取引先コード */}
        <Box>
          <TextField
            label='Gali取引先コード'
            placeholder={PLACEHOLDER_MESSAGES.GALI_CODE_INPUT}
            value={formData.galiSupplierCode || ''}
            onChange={(e) => onUpdateField('galiSupplierCode', e.target.value)}
            error={!isFieldValid('galiSupplierCode')}
            helperText={getFieldError('galiSupplierCode')}
            fullWidth
            slotProps={{
              input: {
                inputProps: { maxLength: 9 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.galiSupplierCode,
                  formData.galiSupplierCode
                ),
              },
            }}
          />
        </Box>

        {/* 正式名称 & 略称 */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <TextField
              label='正式名称'
              placeholder={PLACEHOLDER_MESSAGES.OFFICIAL_NAME_INPUT}
              value={formData.officialName}
              onChange={(e) => onUpdateField('officialName', e.target.value)}
              error={!isFieldValid('officialName')}
              helperText={getFieldError('officialName')}
              fullWidth
              required
              slotProps={{
                input: {
                  inputProps: { maxLength: 200 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.officialName,
                    formData.officialName
                  ),
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <TextField
              label='略称（簡易名称）'
              placeholder={PLACEHOLDER_MESSAGES.ABBREVIATED_NAME_INPUT}
              value={formData.abbreviatedName}
              onChange={(e) => onUpdateField('abbreviatedName', e.target.value)}
              error={!isFieldValid('abbreviatedName')}
              helperText={getFieldError('abbreviatedName')}
              fullWidth
              required
              slotProps={{
                input: {
                  inputProps: { maxLength: 100 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.abbreviatedName,
                    formData.abbreviatedName
                  ),
                },
              }}
            />
          </Box>
        </Box>

        {/* 支店・部門 */}
        <Box>
          <TextField
            label='支店・部門'
            placeholder={PLACEHOLDER_MESSAGES.BRANCH_DEPARTMENT_INPUT}
            value={formData.branchDepartment || ''}
            onChange={(e) => onUpdateField('branchDepartment', e.target.value)}
            error={!isFieldValid('branchDepartment')}
            helperText={getFieldError('branchDepartment')}
            fullWidth
            slotProps={{
              input: {
                inputProps: { maxLength: 100 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.branchDepartment,
                  formData.branchDepartment
                ),
              },
            }}
          />
        </Box>

        {/* 取引先名カナ (半角ｶﾅ)*/}
        <Box>
          <TextField
            label='取引先名カナ (半角ｶﾅ)'
            placeholder={PLACEHOLDER_MESSAGES.COMPANY_NAME_KATAKANA_INPUT}
            value={formData.supplierNameKatakana || ''}
            onChange={(e) => {
              const convertedValue = convertToHalfWidthKatakana(e.target.value);
              onUpdateField('supplierNameKatakana', convertedValue);
            }}
            onBlur={(e) => {
              const currentValue = e.target.value;
              const convertedValue = convertToHalfWidthKatakana(currentValue);
              if (convertedValue !== currentValue) {
                // Use setTimeout to ensure the update happens after the blur event
                setTimeout(() => {
                  onUpdateField('supplierNameKatakana', convertedValue);
                }, 0);
              }
            }}
            error={!isFieldValid('supplierNameKatakana')}
            helperText={getFieldError('supplierNameKatakana')}
            fullWidth
            required
            slotProps={{
              input: {
                inputProps: { maxLength: 200 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.supplierNameKatakana,
                  formData.supplierNameKatakana
                ),
              },
            }}
          />
        </Box>

        {/* 仕入先カテゴリ */}
        <Box>
          <FormControl fullWidth error={!isFieldValid('supplierCategory')} required>
            <InputLabel>仕入先カテゴリ</InputLabel>
            <Select
              value={formData.supplierCategory || ''}
              onChange={(e) =>
                onUpdateField('supplierCategory', e.target.value as SupplierCategory)
              }
              label='仕入先カテゴリ'
              slotProps={{
                input: {
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.supplierCategory,
                    formData.supplierCategory
                  ),
                },
              }}
            >
              {supplierCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('supplierCategory')}</FormHelperText>
          </FormControl>
        </Box>

        {/* セメント共通 */}
        <CommonCement
          value={formData.isCommonCement}
          onChange={(value) => onUpdateField('isCommonCement', value)}
          slotProps={{
            input: {
              sx: getStylesForEditableField(
                !!originalFormData,
                originalFormData?.isCommonCement,
                formData.isCommonCement
              ),
            },
          }}
        />
      </Box>
    </PaperBoxStyles>
  );
};
